import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import type { GeoPoint } from '@app/shared/geo';
import { api, ApiError, fieldIssues } from '../api';
import { can, type Me } from '../auth/me';
import { AddressSearch } from '../events/PlaceField';
import { MapPreview } from '../events/MapPreview';
import { COMPANY_KEY, movedNotice, SETTINGS_RESOURCE, useCompanySettings, type CompanySettings } from './company';
import { sameValues, useUnsavedGuard } from '../ui/unsaved';
import { Notice } from '../ui/Notice';

/**
 * "Magazyn": where the distance of every event is counted from (the Google route; without it "km wg trasy" typed in
 * the event). No road factor (owner 2026-10-23: „nie chce tego”). The km serve the transport price (P5).
 */
export function WarehouseSettings({ me, heading = true }: { me: Me; heading?: boolean }) {
  const queryClient = useQueryClient();
  const current = useCompanySettings().data;
  const [address, setAddress] = useState('');
  const [point, setPoint] = useState<GeoPoint | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  useEffect(() => {
    if (!current) return;
    setAddress(current.warehouseAddress ?? '');
    setPoint(current.warehousePoint ?? null);
  }, [current]);
  const canEdit = can(me, SETTINGS_RESOURCE, 'edit');
  const save = useMutation({
    mutationFn: () => api<CompanySettings>('/api/events/settings', { method: 'PATCH', body: { version: current!.version, warehouseAddress: address.trim(), warehousePoint: point } }),
    onSuccess: async ({ moved, ...next }) => {
      queryClient.setQueryData(COMPANY_KEY, next);
      await queryClient.invalidateQueries({ queryKey: ['events'] });
      setSaved(moved ? movedNotice(moved) : 'Zapisano.');
    },
  });
  function submit(e: FormEvent) {
    e.preventDefault();
    e.stopPropagation();
    setSaved(null);
    save.mutate();
  }
  // owner 2026-10-23 21:14: leaving with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”
  const guardRef = useUnsavedGuard({
    dirty: canEdit && !!current && !sameValues({ address, point }, { address: current.warehouseAddress ?? '', point: current.warehousePoint ?? null }),
    save: async () => {
      if (!current) return false;
      await save.mutateAsync();
      return true;
    },
  });
  const error = save.error instanceof ApiError ? save.error : null;
  return (
    <section className="settings-section" aria-label="Magazyn" data-feature-id="events.settings">
      {heading && <h2>Magazyn</h2>}
      <p className="hint">Skąd liczymy odległość do miejsca wydarzenia („ok. N km od magazynu”) — przyda się przy wycenie transportu.</p>
      <form ref={guardRef} className="settings-form plain" aria-label="Adres magazynu" onSubmit={submit}>
        <fieldset disabled={!canEdit || !current}>
          <AddressSearch
            label="Adres magazynu"
            value={address}
            placeholder="np. Hutnicza 1, Gdynia"
            onChange={(a) => {
              setAddress(a);
              setSaved(null);
            }}
            onPick={(f) => {
              setAddress(f.label);
              setPoint({ lat: f.lat, lng: f.lng });
              setSaved(null);
            }}
          />
          <MapPreview
            label="Mapa magazynu"
            point={point}
            onPick={(p) => {
              setPoint(p);
              setSaved(null);
            }}
          />
          <p className="hint field-hint map-hint">{point ? 'Przesuń pinezkę, jeśli stoi nie tam, gdzie trzeba.' : 'Wyszukaj adres albo kliknij na mapie, gdzie jest magazyn.'}</p>
          {error && (
            <p className="alert" role="alert">
              {error.status === 409 ? 'Ktoś właśnie zmienił ustawienia. Zamknij i otwórz je jeszcze raz.' : fieldIssues(error).map((i) => i.message).join(' ') || error.message}
            </p>
          )}
          {saved && (
            <Notice onClose={() => setSaved(null)}>
              {saved}
            </Notice>
          )}
          {canEdit && (
            <div className="actions">
              <button className="btn primary" type="submit" disabled={save.isPending}>
                {save.isPending ? 'Zapisywanie…' : 'Zapisz magazyn'}
              </button>
            </div>
          )}
        </fieldset>
      </form>
    </section>
  );
}
